import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { DEFAULT_WEEKDAYS, moment } from "@msuite/utils/date";
import { type CrewDefinitionFormSchema, crewDefinitionSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { CrewDefinitionForm } from "@/forms/crew-definition-form";
import { useAuth } from "@/hooks/use-auth";
import { client } from "@/hono";

export function AddCrewDefinitionModal({ isOpen, onClose }: ModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();
	const qc = useQueryClient();
	const methods = useForm<CrewDefinitionFormSchema>({
		resolver: resolver(crewDefinitionSchema),
		defaultValues: {
			name: null,
			organizationId: organization?.id ?? "",
			vehicleId: null,
			startDate: moment().db(),
			endDate: null,
			weekdays: DEFAULT_WEEKDAYS,
			leadUserId: null,
			memberUserIds: [],
		},
	});

	/** Functions */
	async function handleOnSubmit(json: CrewDefinitionFormSchema) {
		const response = await client.crew_definitions.index.$post({ json });
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["crew-definitions"]) });
		onClose();
		methods.reset();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
				title={t("planning.crews.add_crew_definition")}
				data-cy="crew-definition-form"
			>
				<Modal.Body>
					<CrewDefinitionForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
